import { connect, useDispatch } from 'react-redux';
import { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useField } from 'react-final-form';
import { useHistory } from 'react-router';
import { isAfter, isBefore } from 'date-fns';

import { Button } from 'UIPackage/MainModule/components/Button';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ApproveRequiredTypesEnum } from 'APIPackage/KnmModule/enums/ApproveRequiredTypesEnum';
import { IDangerClass248 } from 'APIPackage/DictionariesModule/interfaces/IDangerClass248';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { getNotices } from 'CorePackage/NotificationModule/reducers/noticeReducer';
import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { fetchPlanById } from 'APIPackage/PlanListModule/services/knmPlanApi';
import { IRiskCategory248 } from 'APIPackage/DictionariesModule/interfaces/IRiskCategory248';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { knmSetIsSaveOnProcess } from 'KnmPackage/MainModule/actions/knmActions';

import styles from './CreateErknmModal.module.scss';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import {
  APPROVE_REQUIRED_BLOCK_ID,
  KIND_CONTROL_BLOCK_ID,
  KIND_KNM_BLOCK_ID,
  KNO_ORGANIZATION_BLOCK_ID,
  PROSECUTOR_ORGANIZATION_BLOCK_ID,
  TYPE_BLOCK_ID,
} from '../constants/knmErknmFieldsElementsIds';
import { SelectInputField } from '../../../UIPackage/MainModule/components/SelectInputField';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import { Row } from '../../../UIPackage/FormModule/components/Row';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IKnoOrganizationDictionary } from '../../../APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import {
  getErknmKnmReasonTypesSelectOptions,
  getKnmErknmKindsSelectOptions,
  getKnmErknmSupervisionTypesSelectOptions,
  getKnmOnSaveButtonClick,
  getKnmPlannedActivities,
  getKnmFormRef,
  getKnmErknmDangerClassesSelectOptions,
  getKnmErknmRiskCategoriesSelectOptions,
  getCatalogKnmTypes248SelectOptions,
  getKnmIsWatchOnly,
  getKnmIsSaveOnProcess,
} from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { getDictionariesKnoOrganizationsSelectOptions } from '../../../CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { IDictionarySupervisionTypeSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { IUserOrganisationSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import {
  getAccountOrganizationsWithDistrictData,
  getAccountProsecutorOrganizationsWithDistrictSelectOptions,
} from '../../../CorePackage/AccountModule/selectors/accountSelectors';
import { IKnmFormRef } from '../../../KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmKind } from '../../../APIPackage/DictionariesModule/interfaces/IKnmKind';
import { IKnmReasonTypeSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmReasonTypeSelectOption';
import { IKnmErknmFormReason } from '../interfaces/IKnmErknmFormReason';
import { IKnmTypeSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmTypeSelectOption';
import { IDictionaryKnoOrganizationSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { IKnmErknmForm } from '../interfaces/IKnmErknmForm';
import { ErknmReasonItem } from '../../ErknmReasonsModule/components/ErknmReasonsItem';
import { IUserOrganisation } from '../../../APIPackage/AccountModule/interfaces/IUserOrganisation';
import { CheckNotificationModal } from '../../../CorePackage/MainModule/components/CheckNotificationModal';
import { CheckNotificationTypesEnum } from '../../../CorePackage/MainModule/enums/CheckNotificationTypesEnum';
import { ISelectOption } from '../../../UIPackage/MainModule/components/SelectInput';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS } from '../../../CorePackage/MainModule/constants/dateFormats';
import { formatDate, parseDate } from '../../../CorePackage/MainModule/common/formatDate';
import { KnmTypeCodesEnum } from '../../../APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { Tooltip } from '../../../UIPackage/MainModule/components/Tooltip';
import { KNM_CREATE_ERROR, NOT_HAVE_HIGHT_RISK } from '../../../CorePackage/MainModule/constants/notices';
import { NoticeTypesEnum } from '../../../CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../../CorePackage/NotificationModule/enums/MessageTypesEnum';
import { INotification } from '../../../CorePackage/NotificationModule/interfaces/INotification';
import {
  getUserRoleIsOmbudsman,
  getUserRoleIsProsecutorOrSuperProsecutor,
} from '../../../AuthPackage/MainModule/selectors/authSelectors';
import { ROUTE_KNM_LIST } from '../../../CorePackage/MainModule/routes/routes';
import { ErknmInfoDates } from '../../ErknmInfoModule/components/ErknmInfoDates';
import { Notification } from '../../../CorePackage/NotificationModule/components/Notification';
import { isGeneralProsecutorOGRN } from '../../../CorePackage/MainModule/validators/isOgrn';

interface ICreateErknmModalProps {
  visible: boolean;
  onClose: () => void;
  knoOrganizationErknm: IDictionaryKnoOrganizationSelectOption | undefined;
  kindControl: IDictionarySupervisionTypeSelectOption | undefined;
  reasonsErknm: IKnmErknmFormReason[] | undefined;
  planGuid: IKnmErknmForm['planGuid'];
  startDate: string | undefined;
  stopDate: string | undefined;
  kindKnmCode: KnmKindCodesEnum | undefined;
}

interface ICreateErknmModalStoreProps {
  formRef: IKnmFormRef | undefined;
  isWatchOnly: boolean;
  isProsecutorOrSuperProsecutor: boolean;
  isOmbudsmen: boolean;
  knoOrganizationsSelectOptions: IDictionarySelectOption<IKnoOrganizationDictionary>[] | undefined;
  userProsecutorOrganisationsSelectOptions: IUserOrganisationSelectOption[];
  supervisionTypesSelectOptions: IDictionarySupervisionTypeSelectOption[] | undefined;
  kindsSelectOptions: IDictionarySelectOption<IKnmKind>[] | undefined;
  erknmKnmReasonTypesSelectOptions: IKnmReasonTypeSelectOption[];
  onSaveButtonClick: (() => void) | undefined;
  plannedActivities: boolean | undefined;
  userKnoOrganisations: IUserOrganisation[];
  dangerClassesSelectOptions: IDictionarySelectOption<IDangerClass248>[] | undefined;
  riskCategoriesSelectOptions: IDictionarySelectOption<IRiskCategory248>[] | undefined;
  settings: IAdminSetting[];
  notices: INotification[];
  knmTypes248SelectOptions: IKnmTypeSelectOption[];
  isSaveOnProcess: boolean | undefined;
}

export const CreateErknmModalWithoutStore = ({
  formRef,
  visible,
  onClose,
  isWatchOnly,
  knoOrganizationsSelectOptions,
  userProsecutorOrganisationsSelectOptions,
  supervisionTypesSelectOptions,
  kindsSelectOptions,
  erknmKnmReasonTypesSelectOptions,
  onSaveButtonClick,
  knoOrganizationErknm,
  kindControl,
  plannedActivities,
  reasonsErknm,
  planGuid,
  userKnoOrganisations,
  startDate,
  stopDate,
  isProsecutorOrSuperProsecutor,
  isOmbudsmen,
  dangerClassesSelectOptions,
  settings,
  notices,
  kindKnmCode,
  riskCategoriesSelectOptions,
  knmTypes248SelectOptions,
  isSaveOnProcess,
}: ICreateErknmModalProps & ICreateErknmModalStoreProps) => {
  const push = formRef?.current.mutators.push;
  const history = useHistory();
  const dispatch = useDispatch<Dispatch<RootActions>>();

  if (isOmbudsmen || isProsecutorOrSuperProsecutor) {
    history.push(ROUTE_KNM_LIST);
  }

  const approveRequired = useField('approveRequired', { subscription: { value: true } }).input.value;
  const isApproveRequired = approveRequired.value === ApproveRequiredTypesEnum.REQUIRED;
  const typeErknm = useField('typeErknm', { subscription: { value: true } }).input.value;
  const isUnplanType = typeErknm.value === KnmTypeCodesEnum.VP;

  const [isShowNotice, setIsShowNotice] = useState(false);
  const [createdPlanData, setCreatedPlanData] = useState<string>('');
  const [errorNotice, setErrorNotice] = useState<INotification | undefined>(undefined);
  const [isNotificationModalVisible, setIsNotificationModalVisible] = useState<boolean>(false);
  const [prosecutorOrganizationErvkUuidPlanData, setProsecutorOrganizationErvkUuidPlanData] = useState<string>();

  const extractNumberFromUrl = (url: string): number => {
    const match = url.match(/\/plan\/(\d+)(?:\/|$)/);

    return match ? parseInt(match[1], 10) : 0;
  };

  useEffect(() => {
    if (planGuid) {
      if (window.location.search) {
        const planId = extractNumberFromUrl(window.location.search);

        fetchPlanById(planId).then(response => {
          setCreatedPlanData(response.created);
          if (response.prosecutorOrganizationErvkUuid) {
            setProsecutorOrganizationErvkUuidPlanData(response.prosecutorOrganizationErvkUuid);
          }
        });
      }
    }
  }, [planGuid]);

  const knmTypesTransformFunc = useCallback(
    (items: IKnmTypeSelectOption[]) => {
      if (!knoOrganizationErknm || !kindControl) return [];

      return plannedActivities || items;
    },
    [plannedActivities, knoOrganizationErknm, kindControl],
  );

  const isShow4125Plan = isLaterThanAdminSetting(
    createdPlanData,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_25_STARTS_FROM,
  );

  const hasFirstOrSecondDangerClass = useMemo(() => {
    return dangerClassesSelectOptions?.find(
      i => i.code === '2' || i.code === '1' || i.code === '02' || i.code === '01',
    );
  }, [dangerClassesSelectOptions]);

  const hasHightOrHightestRisk = useMemo(() => {
    return riskCategoriesSelectOptions?.find(i => i.code === '2' || i.code === '1');
  }, [riskCategoriesSelectOptions]);

  const emptyReason: IKnmErknmFormReason = {
    type: undefined,
    text: undefined,
    date: undefined,
    riskIndicators: undefined,
    requirementDetails: undefined,
    dateRequirement: undefined,
    assignmentNumber: undefined,
    assignmentDate: undefined,
    dateBegin: undefined,
    dateEnd: undefined,
    inspectionResults: undefined,
    ervkUuid: undefined,
    listId: uuidv4(),
  };

  useEffect(() => {
    if (!isWatchOnly && push) {
      /**
       * Проверяем reasonsErknm непосредственно из формы, что бы
       * исключить повторное добавление emptyReason
       */
      const currReasonsErknm = formRef.current?.getState()?.values?.reasonsErknm;

      if (!currReasonsErknm || currReasonsErknm?.length < 1) {
        push('reasonsErknm', emptyReason);
      }
    }
  }, [push, isWatchOnly]);

  /**
   * useEffect для поиска и выбора предзаполненного значения для "Контрольный (надзорный) орган"
   */
  useEffect(() => {
    if (userKnoOrganisations && knoOrganizationsSelectOptions) {
      const byDefaultKno = userKnoOrganisations.find(i => i.byDefault);

      if (byDefaultKno) {
        const knoOrg = knoOrganizationsSelectOptions.find(i => i.idStr?.toString() === byDefaultKno.id);

        if (knoOrg) {
          formRef?.current.change('knoOrganizationErknm', knoOrg);
        }
      }
    }
  }, [formRef, userKnoOrganisations, knoOrganizationsSelectOptions]);

  useEffect(() => {
    if (userProsecutorOrganisationsSelectOptions && planGuid === undefined) {
      const byDefault = userProsecutorOrganisationsSelectOptions.find(i => i.byDefault);

      if (byDefault) {
        formRef?.current.change('prosecutorOrganizationErknm', byDefault);
      }
    }
  }, [
    formRef,
    userKnoOrganisations,
    knoOrganizationsSelectOptions,
    userProsecutorOrganisationsSelectOptions,
    planGuid,
  ]);

  const clearStartDateIsBeforeCurrentDate = useCallback(() => {
    if (startDate) {
      const currentDate = formatDate({ date: new Date(), dateFormat: SYSTEM_DATE_FORMAT_FNS });
      const formattedStartDate = formatDate({ date: startDate, dateFormat: SYSTEM_DATE_FORMAT_FNS });

      if (formattedStartDate < currentDate) {
        formRef?.current.change('startDate');
      }
    }
  }, [startDate, formRef]);

  const clearStopDateIsBeforeCurrentDate = useCallback(() => {
    if (stopDate) {
      const currentDate = formatDate({ date: new Date(), dateFormat: SYSTEM_DATE_FORMAT_FNS });
      const formattedStopDate = formatDate({ date: stopDate, dateFormat: SYSTEM_DATE_FORMAT_FNS });

      if (formattedStopDate < currentDate) {
        formRef?.current.change('stopDate');
      }
    }
  }, [stopDate, formRef]);

  const handleKnoOrganizationChange = useCallback(() => {
    formRef?.current.change('kindControl');
    formRef?.current.change('kindKnm');
    formRef?.current.change('reasonsErknm', [emptyReason]);
    formRef?.current.change('approveRequired');
  }, [formRef]);

  const handleKindControlChange = useCallback(
    (value: IDictionarySupervisionTypeSelectOption) => {
      formRef?.current.change('kindControl', {
        ...value,
        label: value.selectedLabel || value.label,
      });
      formRef?.current.change('reasonsErknm', [emptyReason]);
      formRef?.current.change('kindKnm');
      formRef?.current.change('approveRequired');

      if (!value) {
        clearStartDateIsBeforeCurrentDate();
        clearStopDateIsBeforeCurrentDate();
      }
    },
    [formRef, clearStartDateIsBeforeCurrentDate, clearStopDateIsBeforeCurrentDate],
  );

  const handleTypeSelectChange = useCallback(
    (value: ISelectOption) => {
      formRef?.current.change('approved');
      formRef?.current.change('planId');
      formRef?.current.change('planGuid');
      formRef?.current.change('kindKnm');
      formRef?.current.change('reasonsErknm', [emptyReason]);
      formRef?.current.change('reasonDocuments');
      formRef?.current.change('approveRequired');

      if (!value) {
        clearStartDateIsBeforeCurrentDate();
        clearStopDateIsBeforeCurrentDate();
      }
    },
    [formRef, clearStartDateIsBeforeCurrentDate, clearStopDateIsBeforeCurrentDate],
  );

  const handleApproveRequiredChange = useCallback(
    (value: ISelectOption) => {
      if (!value) {
        clearStartDateIsBeforeCurrentDate();
        clearStopDateIsBeforeCurrentDate();
      }
    },
    [clearStartDateIsBeforeCurrentDate, clearStopDateIsBeforeCurrentDate],
  );

  const handleKindKnmChange = useCallback(
    (value: ISelectOption) => {
      if (!value) {
        clearStartDateIsBeforeCurrentDate();
        clearStopDateIsBeforeCurrentDate();
      }
      formRef?.current.change('directDurationDays');
      formRef?.current.change('durationHours');
    },
    [clearStartDateIsBeforeCurrentDate, clearStopDateIsBeforeCurrentDate],
  );

  const reasonsFieldName = 'reasonsErknm[0]';
  const reasonFieldValue: IKnmErknmFormReason | undefined = reasonsErknm ? reasonsErknm[0] : undefined;

  const typesSelectOptions: IKnmReasonTypeSelectOption[] | undefined = erknmKnmReasonTypesSelectOptions
    ?.filter(selectOption => {
      if (startDate && stopDate) {
        const startDateAsDate = parseDate({ date: startDate || '', parseFormat: DATE_FORMAT_LONG_FNS });
        const stopDateAsDate = parseDate({ date: stopDate || '', parseFormat: DATE_FORMAT_LONG_FNS });
        const dateBeginAsDate = parseDate({ date: selectOption.dateBegin || '', parseFormat: SYSTEM_DATE_FORMAT_FNS });
        const dateEndAsDate = parseDate({ date: selectOption.dateEnd || '', parseFormat: SYSTEM_DATE_FORMAT_FNS });

        if (selectOption.dateBegin && selectOption.dateEnd) {
          return (
            isBefore(dateBeginAsDate || 0, startDateAsDate || 0) && isAfter(dateEndAsDate || 0, stopDateAsDate || 0)
          );
        }
        if (selectOption.dateBegin) {
          return isBefore(dateBeginAsDate || 0, startDateAsDate || 0);
        }
      }

      return false;
    })
    .map(selectOption => ({
      ...selectOption,
      isDisabled: reasonsErknm?.some(
        reasonsFieldsValueItem =>
          reasonsFieldsValueItem.type?.id === selectOption.id && selectOption.id !== reasonFieldValue?.type?.id,
      ),
    }));

  const handleCloseNotification = useCallback(() => {
    setIsShowNotice(false);
  }, [setIsShowNotice]);

  const validationHightRisk = useCallback(() => {
    const startDateForm = formRef?.current.getFieldState('startDate')?.value;
    const startDateYear =
      parseDate({ date: startDateForm || '', parseFormat: DATE_FORMAT_LONG_FNS })?.getFullYear() || 0;
    const currentYear = new Date().getFullYear();
    const isNoticeExist = notices.find(i => i.id === MessageTypesEnum.NOT_HAVE_HIGHT_RISK);

    if (!isNoticeExist && !isUnplanType && startDateYear === currentYear) {
      if (!hasHightOrHightestRisk && !hasFirstOrSecondDangerClass) {
        dispatch(
          addNoticeAction({
            id: MessageTypesEnum.NOT_HAVE_HIGHT_RISK,
            renderContent: NOT_HAVE_HIGHT_RISK,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.NOT_HAVE_HIGHT_RISK,
          }),
        );

        return false;
      }
    }

    return true;
  }, [hasHightOrHightestRisk, hasFirstOrSecondDangerClass, isUnplanType, kindKnmCode]);

  /**
   * Обработчик нажатия на кнопку "Сохранить"
   */
  const onClickSaveButton = useCallback(() => {
    if (!onSaveButtonClick || !formRef || !validationHightRisk()) return;

    const isGeneralProsecutor = isGeneralProsecutorOGRN(
      formRef.current.getFieldState('prosecutorOrganizationErknm')?.value?.ogrn,
    );

    const { hasValidationErrors: isValidationErrors } = formRef.current.getState();

    if (isValidationErrors) {
      formRef.current.submit();
      const error: INotification = {
        id: uuidv4(),
        noticeType: NoticeTypesEnum.ERROR,
        messageType: MessageTypesEnum.SAVE_KNM,
        renderContent: KNM_CREATE_ERROR,
      };

      setErrorNotice(error);
      setIsShowNotice(true);

      return;
    }

    setErrorNotice(undefined);
    setIsShowNotice(false);

    if (isUnplanType && isApproveRequired && isGeneralProsecutor) {
      setIsNotificationModalVisible(true);
    } else {
      dispatch(knmSetIsSaveOnProcess(true));
      onSaveButtonClick();
    }
  }, [formRef, onSaveButtonClick, isUnplanType, isApproveRequired, validationHightRisk]);

  /**
   * Функция подтверждения ознакомления с уведомлением
   */
  const onClickButtonApproveNotification = useCallback(() => {
    setIsNotificationModalVisible(false);
    if (onSaveButtonClick) {
      dispatch(knmSetIsSaveOnProcess(true));
      onSaveButtonClick();
    }
  }, [onSaveButtonClick]);

  return (
    <Modal
      modalBodyClassName={styles.MainModal}
      modalClassName={styles.Modal}
      visible={visible}
      onClose={onClose}
      isOutsideClick={false}
    >
      <header className={styles.Header}>
        <Row margin="none">
          <div className={styles.HeaderTitle}>Создание контрольного (надзорного) мероприятия</div>
          {isShowNotice && errorNotice && (
            <div className={styles.NotificationWrapper}>
              <Notification notice={errorNotice} close={handleCloseNotification} />
            </div>
          )}
        </Row>
      </header>
      <ModalBody size="content" border="bottom" overflow="scroll">
        <Row notSpace notWrap>
          <div className={styles.InfoBody}>
            <div className={styles.TooltipIcon}></div>
            <div>Изменение отраженных ниже полей будет заблокировано после сохранения формы</div>
          </div>
        </Row>

        <Row>
          <FieldBlock
            label={getTitleFieldWithRedStar({
              label: `Контрольный (надзорный) орган`,
              field: `knoOrganizationErknm`,
              isPm: false,
              isDecision: false,
              isKnm: true,
            })}
            placementLabel="left"
            id={KNO_ORGANIZATION_BLOCK_ID}
            className={styles.FieldWithOutTooltip}
          >
            <SelectInputField
              id="knoOrganizationErknm"
              name="knoOrganizationErknm"
              options={knoOrganizationsSelectOptions}
              placeholder="Выберите значение"
              isClearable
              isSearchable
              disabled={true}
              className={styles.FullWidth}
              validate={makeRequired('Поле не может быть пустым')}
              onChange={handleKnoOrganizationChange}
            />
          </FieldBlock>
        </Row>

        <Row>
          <FieldBlock
            label={getTitleFieldWithRedStar({
              label: `Орган прокуратуры`,
              field: `prosecutorOrganizationErknm`,
              isPm: false,
              isDecision: false,
              isKnm: true,
            })}
            placementLabel="left"
            id={PROSECUTOR_ORGANIZATION_BLOCK_ID}
            className={styles.FieldWithOutTooltip}
          >
            <SelectInputField
              id="prosecutorOrganizationErknm"
              name="prosecutorOrganizationErknm"
              placeholder="Выберите значение"
              isClearable
              isSearchable
              disabled={true}
              className={styles.FullWidth}
              validate={
                planGuid &&
                prosecutorOrganizationErvkUuidPlanData &&
                !userProsecutorOrganisationsSelectOptions.some(
                  item => item.ervkUuid === prosecutorOrganizationErvkUuidPlanData,
                )
                  ? () => 'Орган прокуратуры отсутствует в личном кабинете пользователя'
                  : undefined
              }
            />
          </FieldBlock>
        </Row>

        <Row notWrap>
          <FieldBlock
            label={getTitleFieldWithRedStar({
              label: `Вид контроля (надзора)`,
              field: `kindControl`,
              isPm: false,
              isDecision: false,
              isKnm: true,
            })}
            placementLabel="left"
            id={KIND_CONTROL_BLOCK_ID}
          >
            <SelectInputField
              id="kindControl"
              name="kindControl"
              options={supervisionTypesSelectOptions}
              placeholder="Выберите значение"
              isSearchable
              disabled={isWatchOnly || isShow4125Plan ? planGuid !== undefined && !!kindControl : false}
              className={styles.FullWidth}
              onChange={handleKindControlChange}
            />
          </FieldBlock>
          <Tooltip
            placement="right"
            type="info"
            content={
              <div className="shared-tooltip-info-content">
                Поле зависит от значения в следующем поле:
                <ul>
                  <p>Контрольный (надзорный) орган</p>
                </ul>
              </div>
            }
          >
            <span className="shared-tooltip-info-icon" />
          </Tooltip>
        </Row>

        <Row notWrap>
          <FieldBlock
            label={getTitleFieldWithRedStar({
              label: `Характер мероприятия`,
              field: `typeErknm`,
              isPm: false,
              isDecision: false,
              isKnm: true,
            })}
            placementLabel="left"
            id={TYPE_BLOCK_ID}
          >
            <SelectInputField
              id="typeErknm"
              name="typeErknm"
              options={knmTypes248SelectOptions.filter(option => option.code !== KnmTypeCodesEnum.PP248P294)}
              placeholder="Выберите значение"
              isClearable
              isSearchable
              disabled={isWatchOnly || planGuid !== undefined}
              className={styles.FullWidth}
              transformFunc={knmTypesTransformFunc}
              onChange={handleTypeSelectChange}
            />
          </FieldBlock>
          <Tooltip
            placement="right"
            type="info"
            content={
              <div className="shared-tooltip-info-content">
                Поле зависит от значения в следующем поле:
                <ul>
                  <p>Контрольный (надзорный) орган</p>
                  <p>Вид контроля (надзора)</p>
                </ul>
              </div>
            }
          >
            <span className="shared-tooltip-info-icon" />
          </Tooltip>
        </Row>

        <ErknmInfoDates
          isApproveRequired={isApproveRequired}
          typeCode={typeErknm.value}
          isCreateModal
          formRef={formRef}
          isNewKnm={true}
          isWatchOnly={isWatchOnly}
        />

        {reasonFieldValue && (
          <FieldBlock
            label={getTitleFieldWithRedStar({
              label: `Основание проведения мероприятия`,
              field: `${reasonsFieldName}.type`,
              isPm: false,
              isDecision: false,
              isKnm: true,
            })}
          >
            <Row notWrap>
              <ErknmReasonItem
                watchOnly={isWatchOnly}
                reasonFieldValue={reasonFieldValue}
                reasonsFieldName={reasonsFieldName}
                sIndex={0}
                typesSelectOptions={typesSelectOptions}
                isNewKnm={true}
                formRef={formRef}
                created={null}
                isCreateModal
              />
              <Tooltip
                placement="right"
                type="info"
                content={
                  <div className="shared-tooltip-info-content">
                    Поле зависит от значения в следующем поле:
                    <ul>
                      <p>Контрольный (надзорный) орган</p>
                      <p>Вид контроля (надзора)</p>
                      <p>Характер мероприятия</p>
                    </ul>
                  </div>
                }
              >
                <span className="shared-tooltip-info-icon" />
              </Tooltip>
            </Row>
          </FieldBlock>
        )}

        <Row margin="none">
          <FieldBlock
            label={getTitleFieldWithRedStar({
              label: `Необходимость согласования`,
              field: `approveRequired`,
              isPm: false,
              isDecision: false,
              isKnm: true,
            })}
            size="half"
            id={APPROVE_REQUIRED_BLOCK_ID}
            className={styles.FieldWithOutTooltip}
          >
            <SelectInputField
              id="approveRequired"
              name="approveRequired"
              placeholder="Выберите значение"
              disabled={true}
              size="medium"
              validate={makeRequired('Поле не может быть пустым')}
              defaultValue={undefined}
              onChange={handleApproveRequiredChange}
            />
          </FieldBlock>
          <FieldBlock
            size="half"
            label={getTitleFieldWithRedStar({
              label: `Вид мероприятия`,
              field: `kindKnm`,
              isPm: false,
              isDecision: false,
              isKnm: true,
            })}
          >
            <Row notWrap>
              <FieldBlock id={KIND_KNM_BLOCK_ID}>
                <SelectInputField
                  id="kindKnm"
                  name="kindKnm"
                  options={kindsSelectOptions}
                  placeholder="Выберите значение"
                  disabled={isWatchOnly}
                  size="medium"
                  onChange={handleKindKnmChange}
                  opensOnTop={true}
                />
              </FieldBlock>
              <Tooltip
                placement="right"
                type="info"
                content={
                  <div className="shared-tooltip-info-content">
                    Поле зависит от значения в следующем поле:
                    <ul>
                      <p>Контрольный (надзорный) орган</p>
                      <p>Вид контроля (надзора)</p>
                      <p>Характер мероприятия</p>
                      <p>Основание проведения мероприятия</p>
                    </ul>
                  </div>
                }
              >
                <span className="shared-tooltip-info-icon" />
              </Tooltip>
            </Row>
          </FieldBlock>
        </Row>
      </ModalBody>
      <ModalActions>
        <Button
          text="Сохранить"
          id="saveButton"
          size="medium"
          disabled={isWatchOnly || isSaveOnProcess}
          onClick={onClickSaveButton}
          color="primary"
        />
        <Button text="Отмена" size="medium" onClick={onClose} />
      </ModalActions>
      <CheckNotificationModal
        notificationType={CheckNotificationTypesEnum.FOR_CREATE_KNM_WITH_GENERAL_PROSECUTOR}
        visible={isNotificationModalVisible}
        onConfirm={onClickButtonApproveNotification}
      />
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): ICreateErknmModalStoreProps => ({
  formRef: getKnmFormRef(state),
  isWatchOnly: getKnmIsWatchOnly(state),
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  isOmbudsmen: getUserRoleIsOmbudsman(state),
  knoOrganizationsSelectOptions: getDictionariesKnoOrganizationsSelectOptions(state),
  supervisionTypesSelectOptions: getKnmErknmSupervisionTypesSelectOptions(state),
  userProsecutorOrganisationsSelectOptions: getAccountProsecutorOrganizationsWithDistrictSelectOptions(state),
  kindsSelectOptions: getKnmErknmKindsSelectOptions(state),
  erknmKnmReasonTypesSelectOptions: getErknmKnmReasonTypesSelectOptions(state),
  onSaveButtonClick: getKnmOnSaveButtonClick(state),
  plannedActivities: getKnmPlannedActivities(state),
  userKnoOrganisations: getAccountOrganizationsWithDistrictData(state).kno,
  dangerClassesSelectOptions: getKnmErknmDangerClassesSelectOptions(state),
  riskCategoriesSelectOptions: getKnmErknmRiskCategoriesSelectOptions(state),
  settings: getAdminFetchSettings(state),
  notices: getNotices(state),
  knmTypes248SelectOptions: getCatalogKnmTypes248SelectOptions(state),
  isSaveOnProcess: getKnmIsSaveOnProcess(state),
});

export const CreateErknmModal = connect(mapStateToProps)(CreateErknmModalWithoutStore);
